/*
 * Copied from Turf.js and trimmed to the polygon-vs-polygon case:
 *   @turf/boolean-intersects, @turf/boolean-disjoint (isPolyInPoly),
 *   @turf/boolean-point-in-polygon (inRing), @turf/line-intersect (intersects).
 *
 * Why we copy instead of calling the packages: turf reaches @turf/bbox to build a
 * search index before comparing edges, and our production build strips the
 * coordinate loop out of bbox, so bbox hands back an empty box. That leaves turf's
 * edge-crossing check blind, and two boxes that merely cross — a narrow drag down a
 * column of chips — come back as not touching. Dev builds keep the loop, so the
 * difference only ever showed up in a real deployment. See sc-26142.
 *
 * The behavior here is meant to match turf exactly, including its edge cases. A test
 * compares this file against @turf/boolean-intersects over thousands of random
 * shapes to keep it that way.
 *
 * ---
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2017 TurfJS
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

// A ring is a closed list of corners: the last one repeats the first.
type Ring = number[][]

// True when the point sits inside the ring. A point exactly on an edge counts as
// inside. Copied from @turf/boolean-point-in-polygon (inRing, ignoreBoundary left off).
export function pointInRing(point: number[], ring: Ring) {
  const [x, y] = point
  const isClosed =
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  const corners = isClosed ? ring.slice(0, ring.length - 1) : ring

  let isInside = false
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const [xi, yi] = corners[i]
    const [xj, yj] = corners[j]

    const onBoundary =
      y * (xi - xj) + yi * (xj - x) + yj * (x - xi) === 0 &&
      (xi - x) * (xj - x) <= 0 &&
      (yi - y) * (yj - y) <= 0
    if (onBoundary) return true

    const crosses =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (crosses) isInside = !isInside
  }
  return isInside
}

// True when the two segments cross. Copied from @turf/line-intersect (intersects).
// Parallel and collinear segments count as no crossing, same as turf — a shared edge
// gets caught by pointInRing instead.
export function segmentsCross(
  a1: number[],
  a2: number[],
  b1: number[],
  b2: number[],
) {
  const [x1, y1] = a1
  const [x2, y2] = a2
  const [x3, y3] = b1
  const [x4, y4] = b2

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1)
  if (denominator === 0) return false

  const uA = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
  const uB = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator
  return uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1
}

// True when the two rings overlap at all. Copied from @turf/boolean-disjoint
// (isPolyInPoly), which is what @turf/boolean-intersects runs for two polygons. Only
// outer rings, so holes are ignored — the shapes we compare never have any.
export function polygonsOverlap(ringA: Ring, ringB: Ring) {
  if (ringA.some((corner) => pointInRing(corner, ringB))) return true
  if (ringB.some((corner) => pointInRing(corner, ringA))) return true

  for (let i = 0; i < ringA.length - 1; i++) {
    for (let j = 0; j < ringB.length - 1; j++) {
      if (segmentsCross(ringA[i], ringA[i + 1], ringB[j], ringB[j + 1])) {
        return true
      }
    }
  }
  return false
}
